import React, { Suspense, lazy, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { FONT_FILES } from '@vidmakr/shared';
import './index.css';
import { installErrorLog } from './lib/bugReport';
import { useSession } from './lib/session';
import { StdbProvider } from './lib/stdb';
import { Toasts } from './components/Toasts';
import { ContextMenuHost } from './components/ContextMenu';
import { SendHost } from '@/components/Integrations';
import { Spinner } from './components/ui';
import { AppShell } from './components/AppShell';
import { NotificationAlerts } from './components/Notifications';
import { DesktopUpdateBanner } from './components/DesktopUpdateBanner';
import { BugReportDialog } from './components/BugReport';
import { LoginPage } from './pages/Login';
import { HomeRedirect } from './pages/Home';
import { ProjectsPage } from './pages/Projects';
import { AssetsPage } from './pages/Assets';
import { SourcesPage } from './pages/Sources';
import { AssetReviewPage } from './pages/AssetReview';
import { MembersPage } from './pages/Members';
import { SettingsPage } from './pages/Settings';
import { BillingPage } from './pages/Billing';
import { ExportsPage } from './pages/Exports';
import { InvitesPage } from './pages/Invites';
import { AccountPage } from './pages/Account';
import { NewOrgPage } from './pages/NewOrg';
import { WorkersPage } from './pages/Workers';

// Keep the page's errors from the start, for bug reports.
installErrorLog();

const EditorPage = lazy(() => import('./editor/EditorPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const LandingPage = lazy(() => import('./pages/Landing').then((m) => ({ default: m.LandingPage })));
const DownloadPage = lazy(() => import('./pages/Download').then((m) => ({ default: m.DownloadPage })));
const ReviewPage = lazy(() => import('./pages/Review').then((m) => ({ default: m.ReviewPage })));
const ProjectReviewsPage = lazy(() => import('./pages/ProjectReviews').then((m) => ({ default: m.ProjectReviewsPage })));

// Register the bundled fonts (same files the export worker uses).
const fontCss = FONT_FILES.map(
  (f) =>
    `@font-face{font-family:'${f.family}';font-style:${f.style};font-weight:${f.weight};font-display:swap;src:url(/fonts/${f.file}) format('woff2');unicode-range:${f.unicodeRange}}`,
).join('\n');
const style = document.createElement('style');
style.textContent = fontCss;
document.head.appendChild(style);

function FullScreenSpinner() {
  return (
    <div className="flex h-full items-center justify-center text-muted">
      <Spinner size={22} />
    </div>
  );
}

function RequireSession({ children }: { children: React.ReactNode }) {
  const status = useSession((s) => s.status);
  const location = useLocation();
  if (status === 'loading') return <FullScreenSpinner />;
  if (status === 'signed-out') {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return (
    <StdbProvider>
      <NotificationAlerts />
      {children}
      {/* Needs the database connection (it reads the organization's integrations). */}
      <SendHost />
    </StdbProvider>
  );
}

/** Signed-out visitors get the landing page; signed-in people go to their organization. */
function Root() {
  const status = useSession((s) => s.status);
  if (status === 'loading') return <FullScreenSpinner />;
  if (status === 'signed-out') return <LandingPage />;
  return (
    <RequireSession>
      <HomeRedirect />
    </RequireSession>
  );
}

function App() {
  const load = useSession((s) => s.load);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <BrowserRouter>
      <Suspense fallback={<FullScreenSpinner />}>
        <Routes>
          <Route path="/" element={<Root />} />
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          {/* Public: the desktop app installers. */}
          <Route path="/download" element={<DownloadPage />} />
          {/* Public: people outside the organization review an export. */}
          <Route path="/r/:token" element={<ReviewPage />} />
          <Route
            path="/p/:projectId"
            element={
              <RequireSession>
                <EditorPage />
              </RequireSession>
            }
          />
          <Route
            path="/p/:projectId/reviews"
            element={
              <RequireSession>
                <ProjectReviewsPage />
              </RequireSession>
            }
          />
          <Route
            element={
              <RequireSession>
                <AppShell />
              </RequireSession>
            }
          >
            <Route path="/new-org" element={<NewOrgPage />} />
            <Route path="/invites" element={<InvitesPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/orgs/:orgId" element={<ProjectsPage />} />
            <Route path="/orgs/:orgId/s/:spaceId" element={<ProjectsPage />} />
            <Route path="/orgs/:orgId/s/:spaceId/f/:folderId" element={<ProjectsPage />} />
            <Route path="/orgs/:orgId/assets" element={<AssetsPage />} />
            <Route path="/orgs/:orgId/sources" element={<SourcesPage />} />
            <Route path="/orgs/:orgId/assets/:assetId/review" element={<AssetReviewPage />} />
            <Route path="/orgs/:orgId/exports" element={<ExportsPage />} />
            <Route path="/orgs/:orgId/members" element={<MembersPage />} />
            <Route path="/orgs/:orgId/settings" element={<SettingsPage />} />
            <Route path="/orgs/:orgId/billing" element={<BillingPage />} />
            <Route path="/orgs/:orgId/workers" element={<WorkersPage />} />
            <Route path="/admin/*" element={<AdminPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toasts />
      <DesktopUpdateBanner />
      <ContextMenuHost />
      <BugReportDialog />
    </BrowserRouter>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
